import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, Trash, Copy, ArrowSquareOut, ArrowsClockwise, CreditCard, Image as ImageIcon, UploadSimple } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useAuth } from '../lib/authContext';
import { computeEventStatus } from '../lib/eventStatus';
import api from '../lib/api';
import { addAccessedCode } from '../lib/tripHistory';
import { resizeImage } from '../lib/imageResize';

export default function OrgAccount() {
  const { organiser, loading, logout, refreshMe } = useAuth();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [syncStates, setSyncStates] = useState({}); // { [eventId]: { payment_state, checkout_url, syncing } }
  const [completing, setCompleting] = useState(null); // eventId currently completing
  // Branding state
  const [brandingUrl, setBrandingUrl] = useState(null);
  const [brandingUploading, setBrandingUploading] = useState(false);
  const [brandingRemoving, setBrandingRemoving] = useState(false);
  const brandingInputRef = useRef(null);
  // Cover photo state
  const [coverUrl, setCoverUrl] = useState(null);
  const [coverUploading, setCoverUploading] = useState(false);
  const [coverRemoving, setCoverRemoving] = useState(false);
  const coverInputRef = useRef(null);

  const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
  const resolveUrl = (url) => url ? (url.startsWith('http') ? url : `${API_BASE}${url}`) : null;

  useEffect(() => {
    setBrandingUrl(resolveUrl(organiser?.branding?.image_url));
    setCoverUrl(resolveUrl(organiser?.branding?.cover_image_url));
  }, [organiser]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadEvents = useCallback(() => {
    if (!organiser) return;
    setEventsLoading(true);
    api.get('/organiser/events').then(r => setEvents(r.data.events || [])).catch(() => {}).finally(() => setEventsLoading(false));
  }, [organiser]);

  useEffect(() => { loadEvents(); }, [loadEvents]);

  // On mount: sync any pending payments and surface their payment_state
  useEffect(() => {
    if (!events.length) return;
    const pendingEvents = events.filter(e => e.latest_payment?.status === 'pending' && e.latest_payment?.revolut_order_id);
    if (!pendingEvents.length) return;
    const syncPending = async () => {
      for (const ev of pendingEvents) {
        try {
          const r = await api.post(`/organiser/payments/by-order/${ev.latest_payment.revolut_order_id}/sync`);
          const d = r.data;
          if (d.payment_state === 'paid') {
            // Reload to show activated event
            setTimeout(loadEvents, 500);
            return;
          }
          setSyncStates(prev => ({ ...prev, [ev.id]: { payment_state: d.payment_state, checkout_url: d.checkout_url } }));
        } catch {}
      }
    };
    syncPending();
  }, [events.length]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <div className="min-h-screen bg-[#F4F6F5] flex items-center justify-center"><div className="w-8 h-8 border-3 border-[#1B4332] border-t-transparent rounded-full animate-spin" /></div>;
  if (!organiser) { navigate('/login'); return null; }

  const handleResendVerification = async () => {
    try {
      await api.post('/organiser/resend-verification', { email: organiser.email });
      toast.success('Verification email sent — check your inbox');
    } catch { toast.error('Failed to send verification email'); }
  };

  const handleLogout = async () => { await logout(); navigate('/home'); };

  const handleDeleteDraft = async (id) => {
    try {
      await api.delete(`/organiser/drafts/${id}`);
      setEvents(prev => prev.filter(e => e.id !== id));
      toast.success('Draft deleted');
    } catch { toast.error('Failed to delete draft'); }
  };

  const handleCopy = (text, label) => {
    navigator.clipboard.writeText(text).then(() => toast.success(`${label} copied`)).catch(() => {});
  };

  const handleRefresh = async (ev) => {
    if (!ev.latest_payment?.revolut_order_id) return;
    setSyncStates(prev => ({ ...prev, [ev.id]: { ...prev[ev.id], syncing: true } }));
    try {
      const r = await api.post(`/organiser/payments/by-order/${ev.latest_payment.revolut_order_id}/sync`);
      const d = r.data;
      if (d.payment_state === 'paid') {
        toast.success('Payment confirmed!');
        loadEvents();
        return;
      }
      setSyncStates(prev => ({ ...prev, [ev.id]: { payment_state: d.payment_state, checkout_url: d.checkout_url, syncing: false } }));
    } catch {
      setSyncStates(prev => ({ ...prev, [ev.id]: { ...prev[ev.id], syncing: false } }));
      toast.error('Failed to check payment status');
    }
  };

  const handleCompletePayment = async (ev) => {
    setCompleting(ev.id);
    try {
      const r = await api.post('/organiser/payments/complete', { draft_trip_id: ev.id });
      const d = r.data;
      if (d.payment_state === 'paid') {
        toast.success('Payment already confirmed!');
        loadEvents();
        return;
      }
      if (d.payment_state === 'processing') {
        setSyncStates(prev => ({ ...prev, [ev.id]: { payment_state: 'processing' } }));
        toast.info('Payment is being processed...');
        return;
      }
      if (d.checkout_url) {
        window.location.href = d.checkout_url;
        return;
      }
      if (d.payment_state === 'unknown') {
        toast.error("Couldn't reach Revolut, try again shortly.");
        setSyncStates(prev => ({ ...prev, [ev.id]: { payment_state: 'unknown' } }));
      }
    } catch (err) {
      if (err?.response?.status === 429) {
        toast.error('Too many attempts — please wait a moment.');
      } else {
        const detail = err?.response?.data?.detail;
        const msg = (err?.response?.status >= 400 && err?.response?.status < 500 && typeof detail === 'string' && detail)
          ? detail
          : 'Failed to process payment. Please try again.';
        toast.error(msg);
      }
    } finally {
      setCompleting(null);
    }
  };

  const daysUntilExpiry = (expiresAt) => {
    if (!expiresAt) return null;
    const exp = new Date(expiresAt);
    const now = new Date();
    return Math.max(0, Math.ceil((exp - now) / 86400000));
  };

  // Sort: paid/complimentary first, then drafts
  const sorted = [...events].sort((a, b) => {
    const order = { paid: 0, complimentary: 0, draft: 1 };
    return (order[a.status] ?? 2) - (order[b.status] ?? 2);
  });

  const renderPendingState = (ev) => {
    const ss = syncStates[ev.id];
    const ps = ss?.payment_state || (ev.latest_payment?.status === 'failed' ? 'failed' : null);
    if (!ps) return null;

    const isCompleting = completing === ev.id;
    const isSyncing = ss?.syncing;

    const stateMessages = {
      payable: "We haven't received your payment yet. Tap Complete payment to finish.",
      processing: "Your payment is being processed by Revolut. We'll update this shortly.",
      failed: "Your previous payment failed.",
      cancelled: "Your previous payment was cancelled.",
      unknown: "Couldn't reach Revolut, try again shortly.",
    };

    const msg = stateMessages[ps];
    if (!msg) return null;

    const showCompleteBtn = ps === 'payable' || ps === 'failed' || ps === 'cancelled';

    return (
      <div className="mt-2 space-y-2" data-testid={`payment-state-${ev.id}`}>
        <p className="text-xs text-[#455561]">{msg}</p>
        <div className="flex gap-2">
          {showCompleteBtn && (
            <Button
              onClick={(e) => { e.stopPropagation(); handleCompletePayment(ev); }}
              disabled={isCompleting}
              size="sm"
              className="h-8 px-3 text-xs bg-[#1B4332] text-white rounded-lg"
              data-testid={`complete-payment-${ev.id}`}
            >
              <CreditCard className="w-3.5 h-3.5 mr-1" weight="bold" />
              {isCompleting ? 'Processing...' : 'Complete payment'}
            </Button>
          )}
          <Button
            onClick={(e) => { e.stopPropagation(); handleRefresh(ev); }}
            disabled={isSyncing}
            size="sm"
            variant="outline"
            className="h-8 px-3 text-xs border-[#E1E6E4] rounded-lg"
            data-testid={`refresh-payment-${ev.id}`}
          >
            <ArrowsClockwise className={`w-3.5 h-3.5 mr-1 ${isSyncing ? 'animate-spin' : ''}`} weight="bold" />
            Refresh
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#F4F6F5] pb-safe" data-testid="account-page">
      <div className="bg-[#1D242B] px-4 py-4 flex items-center gap-3">
        <button onClick={() => navigate('/home')} className="text-white p-1" data-testid="account-back">
          <ArrowLeft className="w-5 h-5" weight="bold" />
        </button>
        <h1 className="text-lg text-white font-bold">My Fore Tour Account</h1>
      </div>
      <div className="px-4 py-6 max-w-lg mx-auto space-y-4">
        <Card className="p-5 bg-white border-[#E1E6E4] rounded-xl space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="font-bold text-[#1D242B]">{organiser.name}</h2>
            {organiser.email_verified
              ? <Badge className="bg-[#E8F5E9] text-[#2E7D32] text-xs">Verified</Badge>
              : <Badge className="bg-[#FFF3E0] text-[#E59000] text-xs">Unverified</Badge>}
          </div>
          <p className="text-sm text-[#455561]">{organiser.email}</p>
          {!organiser.email_verified && (
            <Button onClick={handleResendVerification} variant="outline" size="sm" className="border-[#E1E6E4] text-[#1D242B] rounded-lg" data-testid="resend-verification-btn">
              Resend verification email
            </Button>
          )}
        </Card>

        {/* Branding section */}
        <Card className="p-5 bg-white border-[#E1E6E4] rounded-xl space-y-3" data-testid="branding-section">
          <div className="flex items-center gap-2">
            <ImageIcon className="w-5 h-5 text-[#1B4332]" weight="bold" />
            <h2 className="font-bold text-[#1D242B]">Event Branding</h2>
          </div>
          <p className="text-xs text-[#455561]">Upload your logo or branding image. It will appear on the join screen, event home, and leaderboard for all your events.</p>
          {brandingUrl ? (
            <div className="space-y-3">
              <div className="flex justify-center p-4 bg-[#F4F6F5] rounded-lg">
                <img src={`${brandingUrl}?t=${Date.now()}`} alt="Your branding" className="max-h-[100px] w-auto object-contain" data-testid="branding-preview" />
              </div>
              <div className="flex gap-2">
                <Button onClick={() => brandingInputRef.current?.click()} disabled={brandingUploading} variant="outline" size="sm"
                  className="flex-1 h-9 border-[#E1E6E4] rounded-lg text-xs" data-testid="branding-replace-btn">
                  <UploadSimple className="w-3.5 h-3.5 mr-1" weight="bold" />
                  {brandingUploading ? 'Uploading...' : 'Replace'}
                </Button>
                <Button onClick={async () => {
                  setBrandingRemoving(true);
                  try {
                    await api.delete('/organiser/branding');
                    setBrandingUrl(null);
                    refreshMe();
                    toast.success('Branding removed');
                  } catch { toast.error('Failed to remove branding'); }
                  finally { setBrandingRemoving(false); }
                }} disabled={brandingRemoving} variant="outline" size="sm"
                  className="h-9 px-3 border-[#E1E6E4] rounded-lg text-xs text-[#D32F2F]" data-testid="branding-remove-btn">
                  <Trash className="w-3.5 h-3.5 mr-1" weight="bold" />
                  {brandingRemoving ? '...' : 'Remove'}
                </Button>
              </div>
            </div>
          ) : (
            <Button onClick={() => brandingInputRef.current?.click()} disabled={brandingUploading} variant="outline"
              className="w-full h-20 border-dashed border-2 border-[#E1E6E4] rounded-xl text-[#455561] hover:border-[#1B4332] hover:text-[#1B4332] transition-colors"
              data-testid="branding-upload-btn">
              <div className="flex flex-col items-center gap-1">
                <UploadSimple className="w-5 h-5" weight="bold" />
                <span className="text-xs font-semibold">{brandingUploading ? 'Uploading...' : 'Upload logo or branding image'}</span>
                <span className="text-[10px]">PNG, JPEG, or WebP · Max 5MB</span>
              </div>
            </Button>
          )}
          <input ref={brandingInputRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
            data-testid="branding-file-input"
            onChange={async (e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              e.target.value = '';
              const allowed = ['image/png', 'image/jpeg', 'image/webp'];
              if (!allowed.includes(file.type)) {
                toast.error('Unsupported file type. Use PNG, JPEG, or WebP.');
                return;
              }
              if (file.size > 5 * 1024 * 1024) {
                toast.error('File too large. Max 5MB.');
                return;
              }
              setBrandingUploading(true);
              try {
                const { blob, mimeType } = await resizeImage(file, 800);
                const formData = new FormData();
                const ext = mimeType === 'image/png' ? 'png' : mimeType === 'image/webp' ? 'webp' : 'jpg';
                formData.append('file', blob, `branding.${ext}`);
                const { data } = await api.post('/organiser/branding', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
                if (data.branding?.image_url) {
                  const base = process.env.REACT_APP_BACKEND_URL || '';
                  const url = data.branding.image_url;
                  setBrandingUrl(url.startsWith('http') ? url : `${base}${url}`);
                }
                refreshMe();
                toast.success('Branding image uploaded');
              } catch (err) {
                const detail = err?.response?.data?.detail;
                toast.error(typeof detail === 'string' ? detail : 'Upload failed');
              } finally { setBrandingUploading(false); }
            }}
          />
        </Card>

        {/* Cover photo section */}
        <Card className="p-5 bg-white border-[#E1E6E4] rounded-xl space-y-3" data-testid="cover-section">
          <h3 className="text-sm font-bold text-[#1D242B]">Cover Photo</h3>
          <p className="text-xs text-[#455561]">Replaces the stock golf course image on your event pages. PNG, JPEG, or WebP · Max 10MB.</p>
          {coverUrl ? (
            <div className="space-y-3">
              <div className="rounded-lg overflow-hidden h-32 bg-[#F4F6F5]">
                <img src={`${coverUrl}?t=${Date.now()}`} alt="Cover" className="w-full h-full object-cover object-center" data-testid="cover-preview" />
              </div>
              <div className="flex gap-2">
                <Button onClick={() => coverInputRef.current?.click()} disabled={coverUploading} variant="outline" size="sm"
                  className="flex-1 h-9 border-[#E1E6E4] rounded-lg text-xs" data-testid="cover-replace-btn">
                  <UploadSimple className="w-3.5 h-3.5 mr-1" weight="bold" />
                  {coverUploading ? 'Uploading...' : 'Replace'}
                </Button>
                <Button onClick={async () => {
                  setCoverRemoving(true);
                  try {
                    await api.delete('/organiser/branding/cover');
                    setCoverUrl(null);
                    refreshMe();
                    toast.success('Cover photo removed');
                  } catch { toast.error('Failed to remove cover'); }
                  finally { setCoverRemoving(false); }
                }} disabled={coverRemoving} variant="outline" size="sm"
                  className="h-9 px-3 border-[#E1E6E4] rounded-lg text-xs text-[#D32F2F]" data-testid="cover-remove-btn">
                  <Trash className="w-3.5 h-3.5 mr-1" weight="bold" />
                  {coverRemoving ? '...' : 'Remove'}
                </Button>
              </div>
            </div>
          ) : (
            <Button onClick={() => coverInputRef.current?.click()} disabled={coverUploading} variant="outline"
              className="w-full h-20 border-dashed border-2 border-[#E1E6E4] rounded-xl text-[#455561] hover:border-[#1B4332] hover:text-[#1B4332] transition-colors"
              data-testid="cover-upload-btn">
              <div className="flex flex-col items-center gap-1">
                <UploadSimple className="w-5 h-5" weight="bold" />
                <span className="text-xs font-semibold">{coverUploading ? 'Uploading...' : 'Upload cover photo'}</span>
              </div>
            </Button>
          )}
          <input ref={coverInputRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
            data-testid="cover-file-input"
            onChange={async (e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              e.target.value = '';
              const allowed = ['image/png', 'image/jpeg', 'image/webp'];
              if (!allowed.includes(file.type)) { toast.error('Unsupported file type.'); return; }
              if (file.size > 10 * 1024 * 1024) { toast.error('File too large. Max 10MB.'); return; }
              setCoverUploading(true);
              try {
                const { blob, mimeType } = await resizeImage(file, { maxSide: 1600, quality: 0.8, forceFormat: 'image/jpeg' });
                const formData = new FormData();
                formData.append('file', blob, 'cover.jpg');
                const { data } = await api.post('/organiser/branding/cover', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
                if (data.branding?.cover_image_url) {
                  setCoverUrl(resolveUrl(data.branding.cover_image_url));
                }
                refreshMe();
                toast.success('Cover photo uploaded');
              } catch (err) {
                const detail = err?.response?.data?.detail;
                toast.error(typeof detail === 'string' ? detail : 'Upload failed');
              } finally { setCoverUploading(false); }
            }}
          />
        </Card>

        <Card className="p-5 bg-white border-[#E1E6E4] rounded-xl space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="font-bold text-[#1D242B]">My Events</h2>
            <Button onClick={() => navigate('/wizard/new?step=type')} size="sm" className="bg-[#1B4332] text-white rounded-lg text-xs" data-testid="new-event-btn">+ New Event</Button>
          </div>
          {eventsLoading ? (
            <div className="space-y-2">{[1,2].map(i => <div key={i} className="h-16 bg-[#F4F6F5] rounded-lg animate-pulse" />)}</div>
          ) : sorted.length === 0 ? (
            <p className="text-sm text-[#B8C7C0] italic">No events yet. Create your first event to get started.</p>
          ) : (
            <div className="space-y-2">
              {sorted.map(ev => {
                const isPaid = ev.status === 'paid' || ev.status === 'complimentary';
                const days = daysUntilExpiry(ev.expires_at);
                const price = ev.pricing_snapshot;
                const hasPending = !isPaid && (ev.latest_payment?.status === 'pending' || ev.latest_payment?.status === 'failed');
                return (
                  <div key={ev.id} className={`p-3 rounded-xl border ${isPaid ? 'border-[#C8E6C9] bg-[#F1F8F2]' : 'border-[#E1E6E4] bg-[#F4F6F5]'}`} data-testid={`event-row-${ev.id}`}>
                    <div className="flex items-center gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-[#1D242B] truncate">{ev.name}</p>
                        <p className="text-[10px] text-[#455561]">{ev.start_date} — {ev.end_date} · {ev.player_count} players · {ev.round_count} round{ev.round_count !== 1 ? 's' : ''}</p>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          {(() => { const s = computeEventStatus(ev); return <Badge className={`text-[9px] ${s.color}`}>{s.label}</Badge>; })()}
                          {isPaid ? (
                            <>
                              {ev.code && (
                                <button onClick={() => handleCopy(ev.code, 'Event code')} className="flex items-center gap-1 text-[10px] font-mono text-[#1B4332] bg-[#E8F5E9] px-1.5 py-0.5 rounded" data-testid={`copy-code-${ev.id}`}>
                                  {ev.code} <Copy className="w-3 h-3" weight="bold" />
                                </button>
                              )}
                              {ev.amount_paid_pence != null && (
                                <span className="text-[10px] text-[#455561]">£{(ev.amount_paid_pence / 100).toFixed(2)} paid</span>
                              )}
                            </>
                          ) : (
                            <>
                              {(ev.course_request_pending || ev.course_request_status === 'resolved') && (
                                ev.course_request_status === 'resolved'
                                  ? <Badge className="bg-[#E8F5E9] text-[#2E7D32] text-[9px]">Course added. Choose it to continue</Badge>
                                  : ev.course_request_status === 'rejected'
                                  ? <Badge className="bg-[#FFEBEE] text-[#C62828] text-[9px]">Course not available. Choose another course</Badge>
                                  : <Badge className="bg-[#FFF3E0] text-[#E59000] text-[9px]">Course requested</Badge>
                              )}
                              {price && <span className="text-[10px] text-[#1B4332] font-bold">£{(price.total_pence / 100).toFixed(2)}</span>}
                              {days !== null && <span className="text-[10px] text-[#B8C7C0]">{days}d left</span>}
                            </>
                          )}
                        </div>
                        {hasPending && renderPendingState(ev)}
                      </div>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        {isPaid ? (
                          <Button onClick={() => { addAccessedCode(ev.code); navigate(`/trip/${ev.code}`); }} size="sm" className="h-8 px-3 text-xs bg-[#1B4332] text-white rounded-lg" data-testid={`open-event-${ev.id}`}>
                            <ArrowSquareOut className="w-3.5 h-3.5 mr-1" weight="bold" />Open
                          </Button>
                        ) : (
                          <>
                            <Button onClick={() => navigate(`/wizard/new?step=summary&draft_id=${ev.id}`)} size="sm" variant="outline" className="h-8 px-3 text-xs border-[#E1E6E4] rounded-lg" data-testid={`resume-draft-${ev.id}`}>Resume</Button>
                            <button onClick={() => handleDeleteDraft(ev.id)} className="p-2 text-[#D32F2F] hover:bg-[#FFEBEE] rounded-lg" data-testid={`delete-draft-${ev.id}`}>
                              <Trash className="w-4 h-4" weight="bold" />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>

        <Button onClick={handleLogout} variant="outline" className="w-full h-12 rounded-xl border-[#E1E6E4] text-[#D32F2F] font-semibold" data-testid="logout-btn">
          Log out
        </Button>
        <p className="text-center text-[10px] text-[#B8C7C0] pt-1" data-testid="build-version">
          {(() => {
            try {
              const scripts = document.querySelectorAll('script[src]');
              for (const s of scripts) {
                const src = s.getAttribute('src') || '';
                const m = src.match(/\/static\/js\/main\.([a-f0-9]+)\.js/i);
                if (m) return `Build ${m[1].slice(0, 8)}`;
              }
            } catch {}
            return 'Build dev';
          })()}
        </p>
      </div>
    </div>
  );
}
